import type { ReactNode } from 'react';
import { useEffect, memo, useMemo, useRef } from 'react';

import { SessionContext } from '@/contexts/Session/SessionContext';
import type { UsePersistentReducerOptionsType } from '@/hooks/usePersistentReducer';
import usePersistentReducer from '@/hooks/usePersistentReducer';

import type SessionType from './SessionType';
import defaultSession, { createDefaultSession } from './SessionDefault';
import { sessionReducer } from './SessionReducer';
import { parseJwt } from '@/utils/string';
import { getUserRoles } from '@/contexts/Session/GetUserRoles';
import type { RoleType } from '@/hooks/useNeedRole';
import {
  invalidateTokenRefresh,
  subscribeToTokenRefresh,
} from '@/utils/tokenRefresh';

interface SessionProviderProps {
  children: ReactNode;
  sessionInit?: SessionType;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizePersistedSession(value: unknown): Partial<SessionType> {
  if (!isRecord(value)) {
    return {};
  }

  return {
    ...(typeof value.jwt === 'string' ? { jwt: value.jwt } : undefined),
    ...(isRecord(value.userInfo) ? { userInfo: value.userInfo } : undefined),
    ...(isRecord(value.sessionExtraData)
      ? { sessionExtraData: value.sessionExtraData }
      : undefined),
    ...(typeof value.tmp_member_id === 'string' || value.tmp_member_id === null
      ? { tmp_member_id: value.tmp_member_id }
      : undefined),
    ...(typeof value.member_id === 'string' || value.member_id === null
      ? { member_id: value.member_id }
      : undefined),
    ...(Array.isArray(value.legal_document_acceptances)
      ? { legal_document_acceptances: value.legal_document_acceptances }
      : undefined),
    ...(typeof value.msal === 'string' ? { msal: value.msal } : undefined),
    ...(isRecord(value.me)
      ? { me: value.me as unknown as SessionType['me'] }
      : undefined),
    ...(typeof value.session_id === 'string' || value.session_id === null
      ? { session_id: value.session_id }
      : undefined),
    ...(typeof value.timestamp === 'number'
      ? { timestamp: value.timestamp }
      : undefined),
  };
}

function init(sessionInit: SessionType | undefined) {
  return (persistedSession?: SessionType) => ({
    ...createDefaultSession(),
    ...normalizePersistedSession(persistedSession),
    ...sessionInit,
  });
}

const options: UsePersistentReducerOptionsType = {
  enabled: true,
  key: 'atlas_session',
  storageType: 'sessionStorage',
  writeDelayMs: 0,
};

function SessionProvider({ children, sessionInit }: SessionProviderProps) {
  const [session, dispatch] = usePersistentReducer(
    sessionReducer,
    {
      ...defaultSession,
      ...sessionInit,
    },
    init(sessionInit),
    options
  );

  const parsedJwt = useMemo(() => parseJwt(session.jwt), [session.jwt]);
  const previousJwtRef = useRef(session.jwt);

  useEffect(() => {
    if (previousJwtRef.current !== session.jwt) {
      invalidateTokenRefresh();
      previousJwtRef.current = session.jwt;
    }
  }, [session.jwt]);

  useEffect(
    () =>
      subscribeToTokenRefresh(
        ({ idToken, accessToken, accountUsername, originatingIdToken }) => {
          const refreshedUsername = parseJwt(idToken)?.preferred_username;
          if (
            !originatingIdToken ||
            !refreshedUsername ||
            refreshedUsername.toLowerCase() !== accountUsername.toLowerCase()
          ) {
            return;
          }

          dispatch({
            type: 'REFRESH_TOKENS',
            payload: {
              idToken,
              accessToken,
              expectedCurrentIdToken: originatingIdToken,
            },
          });
        }
      ),
    [dispatch]
  );

  const userEmail = useMemo(() => {
    if (parsedJwt && parsedJwt.preferred_username) {
      return parsedJwt.preferred_username;
    }

    return null;
  }, [parsedJwt]);

  const roles: RoleType[] = useMemo(() => {
    if (parsedJwt && parsedJwt.roles) {
      return parsedJwt.roles as RoleType[];
    }

    return [];
  }, [parsedJwt]);

  const { isAdmin, isSiteManager, isSupport, isReport, hasSupportAccess } =
    getUserRoles(roles);

  const sessionValues = useMemo(
    () => ({
      session,
      dispatch,
      userEmail,
      roles,
      parsedJwt,
      isAdmin,
      isSiteManager,
      isSupport,
      isReport,
      hasSupportAccess,
    }),
    [
      dispatch,
      parsedJwt,
      roles,
      session,
      userEmail,
      isAdmin,
      isSiteManager,
      isSupport,
      isReport,
      hasSupportAccess,
    ]
  );

  return (
    <SessionContext.Provider value={sessionValues}>
      {children}
    </SessionContext.Provider>
  );
}

const SessionProviderMemo = memo(SessionProvider);
SessionProviderMemo.displayName = 'SessionProvider';

export default SessionProviderMemo;
